import React, { useEffect, useState } from "react";
import { Form, Input, Button, Row, Col, message } from "antd";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { hosturl } from "../libs/Constants";
import dayjs from "dayjs";

const PaymentGateway = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    name,
    gender,
    phoneNumber,
    zodiac,
    date,
    time,
    place,
    familyMembers,
  } = location.state || {};

  const [form] = Form.useForm();
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchUserDetails();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchAmount();
    }
  }, [userId, location.state?.familyMembers]);

  const fetchAmount = async () => {
    try {
      const response = await axios.get(`${hosturl}/appointmentamount`);
      if (response.data && response.data.length > 0) {
        let baseAmount = response.data[0].amount;
        const familyMemberCount = location.state?.familyMembers?.length || 0;
        let totalAmount = baseAmount * (1 + familyMemberCount);

        setAmount(totalAmount);
        form.setFieldsValue({
          amount: totalAmount,
        });
      } else {
        message.error("Failed to fetch amount.");
      }
    } catch (err) {
      console.error("Error fetching amount:", err);
      message.error("Failed to fetch amount.");
    }
  };

  const fetchUserDetails = async () => {
    if (!token) {
      setError("Please log in to view your profile.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${hosturl}/user-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data.user || {});
      setUserId(response.data.user._id);

      const email = response.data.user.email;
      const emailParts = email.split("@");
      const username = emailParts[0];
      const lastName = emailParts[1] ? emailParts[1].split(".")[0] : "";

      form.setFieldsValue({
        buyerEmail: email,
        buyerPhone: response.data.user.phoneNumber,
        buyerFirstName: username,
        buyerLastName: lastName,
      });
    } catch (err) {
      setError("An unexpected error occurred. Please login.");
      message.error("Please log in to continue.");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    if (!userId) {
      message.error("User ID is required. Please log in.");
      return;
    }

    if (!amount) {
      message.error("Amount is required.");
      return;
    }

    // Format family members
    const formattedfamilyMembers = familyMembers?.map((member) => ({
      name: member.name,
      gender: member.gender,
      zodiac: member.zodiac,
      dob: member.date,
      time: member.time,
    })) || [];

    // Prepare payment data
    const paymentData = {
      userId,
      name,
      gender,
      phoneNumber,
      zodiac,
      date: date ? (date.$d ? date.$d.toISOString() : date) : null,
      time: time ? (time.$d ? time.$d.toISOString() : time) : null,
      place,
      amount,
      familyMembers: formattedfamilyMembers,
      user,
      buyerEmail: values.buyerEmail,
      buyerPhone: values.buyerPhone,
      buyerFirstName: values.buyerFirstName,
      buyerLastName: values.buyerLastName,
    };

    const shopPaymentUrl = 'https://thantrajyothishyalayam.com/raosastro-payment';
    const encodedData = encodeURIComponent(JSON.stringify(paymentData));
    
    message.loading('Redirecting to secure payment gateway...');
    
    setTimeout(() => {
      window.location.href = `${shopPaymentUrl}?data=${encodedData}`;
    }, 500);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p>Loading payment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <p style={{ color: "red" }}>{error}</p>
        <Button onClick={() => navigate("/signin")}>Go to Login</Button>
      </div>
    );
  }

  return (
    <div
      className="wrapper"
      style={{
        padding: "20px",
        maxWidth: "800px",
        margin: "0 auto",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#fff",
      }}
    >
      <h2 style={{ color: "#333", textAlign: "center", marginBottom: "30px" }}>
        Review Payment Details
      </h2>

      {/* Booking Summary */}
      <div
        style={{
          backgroundColor: "#f9f9f9",
          padding: "20px",
          borderRadius: "8px",
          marginBottom: "30px",
        }}
      >
        <h3 style={{ fontSize: "18px", marginBottom: "15px" }}>Booking Summary</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <div>
            <strong>Name:</strong> {name}
          </div>
          <div>
            <strong>Phone:</strong> {phoneNumber}
          </div>
          <div>
            <strong>Zodiac:</strong> {zodiac}
          </div>
          <div>
            <strong>Gender:</strong> {gender}
          </div>
          {familyMembers && familyMembers.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <strong>Family Members:</strong> {familyMembers.length}
            </div>
          )}
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Email *"
              name="buyerEmail"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Please enter a valid email!",
                },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Phone *"
              name="buyerPhone"
              rules={[
                {
                  required: true,
                  pattern: /^[0-9]*$/,
                  message: "Please enter a valid phone number!",
                },
              ]}
            >
              <Input maxLength={10} size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="First Name *"
              name="buyerFirstName"
              rules={[
                { required: true, message: "Please enter your first name!" },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Last Name *"
              name="buyerLastName"
              rules={[
                { required: true, message: "Please enter your last name!" },
              ]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Amount"
              name="amount"
              rules={[{ required: true, message: "Please enter amount!" }]}
            >
              <Input
                type="text"
                size="large"
                style={{ 
                  textAlign: "right", 
                  backgroundColor: "#f5f5f5",
                  fontSize: "18px",
                  fontWeight: "bold"
                }}
                readOnly
                prefix="₹"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            style={{
              width: "100%",
              height: "50px",
              backgroundColor: "#E1B440",
              borderColor: "#E1B440",
              fontSize: "18px",
              fontWeight: "bold",
            }}
          >
            Proceed to Payment
          </Button>
        </Form.Item>

        <div style={{ textAlign: "center", marginTop: "10px" }}>
          <small style={{ color: "#666" }}>
            🔒 Secure payment powered by Razorpay
          </small>
        </div>
      </Form>
    </div>
  );
};

export default PaymentGateway;